import * as S from './styles';
import { colors } from '@/constants';
import { useCalcPrice } from '@/talons';
import { buildImageSrc, buildImageSrcError } from '@/shared';
import { CartItems } from '@/hooks/CartContext';
import { useClipboard } from '@/shared/clipboard';
import { RiInformationLine } from 'react-icons/ri';
import { RiCalendarTodoLine } from 'react-icons/ri';
import { AddToCart, Packaging, StPrice, Stock } from '@/components';
import { formatCurrency, getEffectivePriceRecordForPack } from '@/shared';
import { useMemo, useState, useEffect, memo, useCallback, useRef } from 'react';
import { useCart, useGlobals, useDiscountFlex, useClient, useStock, useModalAttributes, useModalFlex, useTables } from '@/hooks';
import { CardDescription, CardMode, ClientProps, PackagesTypesProps, PackagingProductProps, PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';

interface AddProductProps {
  sku: number;
  quantity: number;
  option_uid: string;
  uid: string;
  category_id: number;
}

interface UpdateProductAmountProps {
  sku: number;
  uid: string;
  quantity: number;
}

interface Product {
  showTime?: boolean;
  product: ProductProps;
  showInfos?: boolean;
  showFlex?: boolean;
  direction?: CardMode;
  paymentCod?: number | string;
  removeProductFromCart?: ({ uid }: { uid: string }) => void;
  updateProductAmount?: ({ sku, uid, quantity }: UpdateProductAmountProps) => void;
  addProductToCart?: ({ sku, quantity, option_uid, uid, category_id }: AddProductProps) => void;
  handleModalFlex?: (product: ProductProps, pack: PackagesTypesProps, packQty: number) => void;
  onProductNavigate?: () => void;
}

interface PropsFlex extends Product {
  substTrib: TaxSubstitutionProps[];
}

interface PropsNoFlex extends Product {
  substTrib?: never;
}

export type Props = PropsFlex | PropsNoFlex;

// Nome + descrição
const ProductInfo = memo(
  ({
    product,
    cardDescription,
    onProductNavigate,
  }: {
    product: ProductProps;
    ean: string | undefined;
    commissionFlag: string | null;
    cardDescription: CardDescription;
    onProductNavigate?: () => void;
  }) => {
    return (
      <S.Div>
        <S.ProductLink to={`/${product.COD_PRODUTO}`} onClick={onProductNavigate}>
          <S.DescProduct>
            {cardDescription === CardDescription.DC ? (product.DESC_PRODUTO_BUSCA || product.DESC_PRODUTO) : product.DESC_PRODUTO || product.DESC_PRODUTO_BUSCA}
          </S.DescProduct>
        </S.ProductLink>
      </S.Div>
    );
  }
);

// Última compra
const LastPurchase = memo(
  ({ lastPurchase, direction, label = 'itens' }: { lastPurchase: any; direction: CardMode; label: 'itens' | 'unidades' }) =>
    lastPurchase && (
      <S.LastPurchaseBox $grid={direction === 'grid'}>
        <S.LastPurchaseRow>
          <RiCalendarTodoLine size={16} />
          {direction === 'list' && <S.LastPurchaseText>Última venda:</S.LastPurchaseText>}
          <S.Space />
          <S.LastPurchaseText>{lastPurchase?.date}</S.LastPurchaseText>
        </S.LastPurchaseRow>
        <S.LastPurchaseRow>
          <S.LastPurchaseBold>
            {lastPurchase?.quantity}{' '}
            {lastPurchase?.quantity > 1 ? (label === 'itens' ? 'itens' : 'unidades') : label === 'itens' ? 'item' : 'unidade'}
          </S.LastPurchaseBold>
          <S.LastPurchaseBold>{formatCurrency({ value: lastPurchase?.value })}</S.LastPurchaseBold>
        </S.LastPurchaseRow>
      </S.LastPurchaseBox>
    )
);

// Preço + Flex + Estoque
const PriceContent = memo(
  ({
    product,
    hasStock,
    price,
    unityPrice,
    discountPrice,
    packQty,
    effectiveFinalPrice,
    selectedClient,
    showFlex,
    showInfosFlex,
    rawQuantity,
    packQuantity,
    direction,
    handleOpenFlexModal,
    flexState,
    flexPercentual,
  }: {
    product: ProductProps;
    showOldPrice: boolean;
    hasStock: boolean;
    price: PriceProductProps;
    unityPrice: number;
    discountPrice: number;
    packQty: number;
    effectiveFinalPrice: number;
    effectiveRecord: PriceProductProps | null;
    selectedClient: ClientProps;
    uid: string;
    discounts: any;
    showFlex: boolean;
    showInfosFlex: boolean;
    rawQuantity: number;
    packQuantity: number;
    direction: CardMode;
    handleOpenFlexModal: () => void;
    flexState?: 'positive' | 'negative';
    flexPercentual?: number;
  }) => (
    <S.PriceContent $isList={direction === 'list'} $bordered={hasStock && effectiveFinalPrice > 0}>
      <S.RowPrice>
        {packQty && effectiveFinalPrice && hasStock && (
          <>
            <S.ColPrice>
              <S.UnitPrice>
                {formatCurrency({
                  value: unityPrice,
                })}
                /un
              </S.UnitPrice>
              <S.Price>
                {formatCurrency({
                  value: effectiveFinalPrice,
                })}
              </S.Price>

              {price?.DESTACA_ST === 'S' && selectedClient?.COD_LOJA === 743 && price?.VLR_PRECO_CALCULADO && (
                <StPrice
                  precoSt={price?.ST_VLR_FINAL || price?.ST_VLR_FINAL_CART}
                  precoCalculado={price?.VLR_PRECO_CALCULADO}
                  cliente={selectedClient}
                  codTributacaoProduto={price?.COD_TRIBUTACAO_PRODUTO}
                  precoPauta={price?.VLR_PAUTA}
                  descontoFlex={flexPercentual || 0}
                />
              )}

              <Stock stockLeft={hasStock ? Math.floor(rawQuantity / packQuantity) : 0} extraMarginTop={false} />
            </S.ColPrice>
          </>
        )}
      </S.RowPrice>

      {/* Preserva o espaço do botão Flex mesmo quando não exibido */}
      {showFlex && showInfosFlex && hasStock && effectiveFinalPrice > 0 ? (
        <S.ButtonFlex
          $isdisabled={!(discountPrice || effectiveFinalPrice) || !hasStock}
          disabled={!(discountPrice || effectiveFinalPrice) || !hasStock}
          data-test={`button-flex-${product.COD_PRODUTO}`}
          onClick={handleOpenFlexModal}
          $isList={direction === 'list'}
          $flexState={flexState}
        >
          <S.EditIcon size={20} />
          {flexPercentual !== 0 ? `${flexPercentual?.toFixed(2)}%` : 'Flex'}
        </S.ButtonFlex>
      ) : (
        // Placeholder vazio: mantém o espaço sem exibir conteúdo visível
        <S.ButtonFlex
          $isdisabled={true}
          disabled
          aria-hidden="true"
          data-test={`button-flex-${product.COD_PRODUTO}-placeholder`}
          onClick={() => { }}
          $isList={direction === 'list'}
          $flexState={undefined}
          style={{
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        />
      )}
    </S.PriceContent>
  )
);

const RenderProducts = ({
  product,
  substTrib,
  paymentCod,
  showFlex = true,
  showInfos = true,
  direction = 'grid',
  onProductNavigate,
}: Props) => {
  const { tables } = useTables();
  const { stockLeft } = useStock();
  const { getPrices } = useCalcPrice();
  const { selectedClient } = useClient();
  const { copyToClipboard } = useClipboard();
  const { openModal: openModalFlex } = useModalFlex();
  const { paymentCode, cardDescription } = useGlobals();
  const { getLimit, cartDetails, getStockMsg } = useCart();
  const { getDiscountProduct, discounts } = useDiscountFlex();
  const { openModal: openModalAttributes } = useModalAttributes();

  const image = useMemo(() => product.IMAGEM || tables.current.imagens?.[product.COD_PRODUTO], [product.IMAGEM, tables, product.COD_PRODUTO]);
  
  const isChangedPack = useRef(false);
  
  const modePayment = useMemo(() => Number(paymentCode || paymentCod), [paymentCode, paymentCod]);
  
  const [pack, setPackage] = useState(product.EMBALAGEM?.[0]?.SG_EMBALAGEM);
  const [packQuantity, setPackQuantity] = useState(
    (product.EMBALAGEM?.[0]?.QTD_MULTIPLO_VENDA > 1
      ? product?.EMBALAGEM?.[0]?.QTD_MULTIPLO_VENDA
      : product?.EMBALAGEM?.[0]?.QTD_EMBALAGEM) ?? 0
  );

  const setPackageIntercept = useCallback((val: PackagesTypesProps) => {
    isChangedPack.current = true; // marca mudança manual
    setPackage(val);
  }, []);

  const setPackQuantityIntercept = useCallback((val: number) => {
    isChangedPack.current = true; // marca mudança manual
    setPackQuantity(val);
  }, []);

  const packing = useMemo(
    () =>
      product.EMBALAGEM.find((p) => {
        if (pack === 'PK') {
          return pack === p?.SG_EMBALAGEM && packQuantity === p?.QTD_MULTIPLO_VENDA;
        }
        return pack === p?.SG_EMBALAGEM && packQuantity === p?.QTD_EMBALAGEM;
      }) as PackagingProductProps,
    [product, pack, packQuantity]
  );

  const productPrices = useMemo(() => getPrices(product), [getPrices, product, modePayment, direction]);

  const price = useMemo(
    () =>
      (productPrices.find((p) => {
        const clientSegs = selectedClient?.COD_SEGMENTO?.map((s: any) => s.cod_segmento) || [];
        // Segmento 999: curinga. Prod: restringir a 60/61 se necessário.
        const segs = p?.NRO_SEGMENTO === 999 || clientSegs.includes(p?.NRO_SEGMENTO);
        if (pack === 'PK') {
          return (
            p.SG_EMBALAGEM === pack &&
            packQuantity === p.QTD_MULTIPLO_VENDA &&
            p.NROCONDICAOPAGTO === modePayment &&
            p.COD_LOJA === selectedClient?.COD_LOJA &&
            segs
          );
        }
        return (
          p.SG_EMBALAGEM === pack &&
          p.QTD_EMBALAGEM === packQuantity &&
          p.NROCONDICAOPAGTO === modePayment &&
          segs
        );
      }) as PriceProductProps) || ({} as PriceProductProps),
    [productPrices, pack, packQuantity, modePayment, selectedClient]
  );
  
  const discountPrice = useMemo(() => {
    const emb = product.EMBALAGEM.find((i) => {
      if (pack === 'PK') {
        return i.SG_EMBALAGEM === pack && packQuantity === i?.QTD_MULTIPLO_VENDA;
      }
      return pack === i?.SG_EMBALAGEM && packQuantity === i?.QTD_EMBALAGEM;
    });

    return getDiscountProduct({
      package: pack,
      productId: product.COD_PRODUTO,
      quantity: emb?.QTD_MULTIPLO_VENDA && emb?.QTD_MULTIPLO_VENDA > 1 ? emb?.QTD_MULTIPLO_VENDA : emb?.QTD_EMBALAGEM || 0,
    })?.discountPrice?.[modePayment];
  }, [getDiscountProduct, pack, packQuantity, modePayment, product.COD_PRODUTO, product.EMBALAGEM]);

  const flexPercentual = useMemo(() => {
    const emb = product.EMBALAGEM.find((i) => {
      if (pack === 'PK') {
        return i.SG_EMBALAGEM === pack && packQuantity === i?.QTD_MULTIPLO_VENDA;
      }
      return i.SG_EMBALAGEM === pack && packQuantity === i?.QTD_EMBALAGEM;
    });

    const record = getDiscountProduct({
      package: pack,
      productId: product.COD_PRODUTO,
      quantity: emb?.QTD_MULTIPLO_VENDA && emb?.QTD_MULTIPLO_VENDA > 1 ? emb?.QTD_MULTIPLO_VENDA : emb?.QTD_EMBALAGEM || 0,
    });

    return typeof record?.percentual === 'number' ? record?.percentual : 0;
  }, [getDiscountProduct, pack, packQuantity, product.COD_PRODUTO, product.EMBALAGEM]);

  const flexState = useMemo<undefined | 'positive' | 'negative'>(() => {
    if (flexPercentual > 0) return 'positive';
    if (flexPercentual < 0) return 'negative';
    return undefined;
  }, [flexPercentual]);

  const commissionFlag = useMemo(() => {
    if (!product || !pack) return null;

    const foundItem = product.EMBALAGEM.find((p) => {
      if (pack === 'PK') {
        return p.SG_EMBALAGEM === pack && p.QTD_MULTIPLO_VENDA === packQuantity;
      }
      return p.SG_EMBALAGEM === pack && p.QTD_EMBALAGEM === packQuantity;
    });

    return foundItem ? foundItem.SG_COMISSAO : null;
  }, [product, pack, packQuantity]);

  const ean = useMemo(() => product.ATRIBUTOS.find((at) => at.ean_grp)?.ean_grp, [product]);

  const sku = useMemo(() => product.COD_PRODUTO, [product]);

  const quantity = useMemo(() => {
    const items = cartDetails?.[selectedClient?.COD_CLIENTE]?.items;
    if (!items?.length) return 0;

    return (
      items.find((i) => {
        const [emb, qtd] = i?.option_uid?.split('_') as [PackagesTypesProps, string];

        if (packing?.QTD_MULTIPLO_VENDA > 1 && emb === 'PK') {
          return (
            i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_MULTIPLO_VENDA?.toString()
          );
        }

        return i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_EMBALAGEM?.toString();
      })?.quantity || 0
    );
  }, [
    cartDetails,
    selectedClient?.COD_CLIENTE,
    packing?.QTD_MULTIPLO_VENDA,
    packing?.SG_EMBALAGEM,
    packing?.QTD_EMBALAGEM,
    product?.COD_PRODUTO,
  ]);

  const hasMultiplePacking = useMemo(
    () =>
      product?.EMBALAGEM?.length > 1 &&
        cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.find((i: CartItems) => i?.sku === product?.COD_PRODUTO)
        ? cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.find((i: CartItems) => i?.sku === product?.COD_PRODUTO)
        : null,
    [product, selectedClient, cartDetails]
  );

  const hasExpiring = useMemo(() => getStockMsg(product?.COD_PRODUTO), [getStockMsg, product?.COD_PRODUTO]);

  const expiringSeverity = useMemo<'danger' | 'warning' | 'normal'>(() => {
    const msg = hasExpiring || '';
    const match = msg.match(/(\d+)/);
    const days = match ? Number(match[1]) : NaN;

    if (!isNaN(days)) {
      if (days >= 0 && days <= 60) return 'danger';
      if (days <= 120 && days >= 61) return 'warning';
    }
    return 'normal';
  }, [hasExpiring]);

  const totalQuantity = useMemo(() => {
    if (!hasMultiplePacking) return 0;

    let count = 0;
    cartDetails?.[selectedClient?.COD_CLIENTE]?.items?.reduce((acc, currentValue) => {
      if (currentValue?.sku === product?.COD_PRODUTO) {
        const [, qtd] = currentValue?.option_uid?.split('_');
        const qty = Number(qtd);
        const val = currentValue?.quantity * qty;
        count = acc += val;
      }
      return acc;
    }, 0);
    return count;
  }, [cartDetails, hasMultiplePacking, product?.COD_PRODUTO, selectedClient?.COD_CLIENTE, direction]);

  const rawCurrentAddedQty = useMemo(() => {
    const maximum = packing?.QTD_MULTIPLO_VENDA > 1 && pack === 'PK';

    if (hasMultiplePacking && hasMultiplePacking?.sku) {
      return totalQuantity;
    }

    if (!hasMultiplePacking?.sku && quantity) {
      return maximum ? packing?.QTD_MULTIPLO_VENDA * quantity : packing?.QTD_EMBALAGEM * quantity;
    }

    return 0;
  }, [hasMultiplePacking, pack, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA, quantity, totalQuantity]);

  const rawQuantity = useMemo(() => {
    const limit = getLimit(product?.COD_PRODUTO);
    const stock = stockLeft?.[product?.COD_PRODUTO]?.stock_left;

    if (stockLeft?.[product?.COD_PRODUTO]) {
      if (stock && stock > 0) {
        if (rawCurrentAddedQty) {
          return stock <= rawCurrentAddedQty ? 0 : stock - rawCurrentAddedQty;
        }
        return stock;
      }
      return 0;
    }

    if (rawCurrentAddedQty) {
      return limit <= rawCurrentAddedQty ? 0 : limit - rawCurrentAddedQty;
    }
    return limit;
  }, [getLimit, product?.COD_PRODUTO, stockLeft, rawCurrentAddedQty]);

  const packRequiredQty = useMemo(() => {
    const multiple = packing?.QTD_MULTIPLO_VENDA > 1 && pack === 'PK';
    return multiple ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;
  }, [pack, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA]);

  const hasStock = useMemo(() => {
    if (!packRequiredQty) return false;
    if (rawQuantity == null) return false;

    if (rawQuantity >= packRequiredQty) return true;

    if (rawCurrentAddedQty > 0) return true;

    return false;
  }, [rawQuantity, packRequiredQty, rawCurrentAddedQty]);

  const showInfosFlex = useMemo(() => {
    if (!product.EMBALAGEM) return false;

    const embalagem = product.EMBALAGEM.find((p) => {
      if (pack === 'PK') {
        return p.SG_EMBALAGEM === pack && packQuantity === p.QTD_MULTIPLO_VENDA;
      }
      return p.SG_EMBALAGEM === pack && packQuantity === p.QTD_EMBALAGEM;
    });

    if (!embalagem) return false;
    return embalagem.PERC_MAX_FLEX !== 0 && embalagem.PERC_MIN_FLEX !== 0;
  }, [pack, product.EMBALAGEM, packQuantity, direction]);

  const {
    effectiveRecord,
    effectiveFinalPrice,
    unitPrice: unityPrice,
  } = useMemo(() => {
    const result = getEffectivePriceRecordForPack({
      prices: productPrices,
      pack,
      packQty: packRequiredQty || 0,
      paymentCode: modePayment,
      selectedClient: selectedClient,
    });

    const final = discountPrice || result.effectiveFinalPrice || 0;
    const unit = packRequiredQty && final ? Number((final / packRequiredQty).toFixed(2)) : result.unitPrice;

    return {
      effectiveRecord: result.effectiveRecord,
      effectiveFinalPrice: final,
      unitPrice: unit,
    };
  }, [productPrices, pack, packRequiredQty, modePayment, discountPrice]);

  const uid = useMemo(() => {
    const emb =
      packing?.QTD_MULTIPLO_VENDA && packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;

    return `${product?.COD_PRODUTO}-${pack}-${emb}`;
  }, [product?.COD_PRODUTO, pack, packing]);

  const showSelo = useMemo(() => !!price?.VLR_FINAL && price?.TP_PROMO !== 'B', [price]);

  const showOldPrice = useMemo(() => price?.TP_PROMO !== 'B' && price?.VLR_PRECO > price.VLR_FINAL, [price]);

  const lastPurchase = useMemo(() => {
    if (!product?.ultimaCompra) return null;
    if (product?.ultimaCompra?.SEQPESSOA !== selectedClient?.COD_CLIENTE) return null;

    return {
      date: new Date(product?.ultimaCompra?.DT_COMPRA).toLocaleDateString('pt-BR'),
      quantity: product?.ultimaCompra?.QTDATENDIDA,
      value: product?.ultimaCompra?.VALOR,
    };
  }, [product, selectedClient?.COD_CLIENTE]);

  const handleOpenAttributesModal = () => {
    openModalAttributes({ product, pack, packQuantity, price })
  };

  const handleOpenFlexModal = useCallback(() => {
    openModalFlex({ product, pack, packQuantity, price, substTrib: substTrib || [], hasStock, rawQuantity });
  }, [openModalFlex, product, pack, packQuantity, price, substTrib, hasStock, rawQuantity]);

  useEffect(() => {
    const hasEmb = product && product.EMBALAGEM && product.EMBALAGEM.length > 0;

    if (hasEmb && !pack && !packQuantity) {
      setPackage(product.EMBALAGEM[0]?.SG_EMBALAGEM);
      setPackQuantity(
        product.EMBALAGEM[0]?.QTD_MULTIPLO_VENDA > 1
          ? product.EMBALAGEM[0]?.QTD_MULTIPLO_VENDA
          : product.EMBALAGEM[0]?.QTD_EMBALAGEM
      );
    }
  }, [product, pack, packQuantity]);

  // auto-ajuste de embalagem quando não tem preço efetivo ou não tem estoque
  useEffect(() => {
    if (isChangedPack.current) return;

    const hasEmb = product?.EMBALAGEM?.length > 0;
    if (!hasEmb) return;

    const currentQty = pack === 'PK' ? packing?.QTD_MULTIPLO_VENDA || 0 : packing?.QTD_EMBALAGEM || 0;
    const hasEffectivePrice = Boolean(discountPrice || price?.VLR_FINAL || effectiveFinalPrice);
    const canKeepCurrent = Boolean(pack && packQuantity && hasStock && hasEffectivePrice && currentQty > 0);

    const totalStock = (stockLeft?.[product?.COD_PRODUTO]?.stock_left ?? getLimit(product?.COD_PRODUTO)) || 0;
    // estoque restante considerando o que já está no carrinho para este produto
    const remainingUnits = Math.max(0, totalStock - (totalQuantity || 0));

    const findPriceRecordForEmb = (emb: PackagingProductProps) => {
      const qty = emb.QTD_MULTIPLO_VENDA > 1 ? emb.QTD_MULTIPLO_VENDA : emb.QTD_EMBALAGEM;
      const rec = productPrices.find((pr) => {
        if (emb.SG_EMBALAGEM === 'PK') {
          return pr.SG_EMBALAGEM === 'PK' && pr.QTD_MULTIPLO_VENDA === qty && pr.NROCONDICAOPAGTO === modePayment;
        }
        return pr.SG_EMBALAGEM === emb.SG_EMBALAGEM && pr.QTD_EMBALAGEM === qty && pr.NROCONDICAOPAGTO === modePayment;
      });
      return rec;
    };

    // Ordenar por maior quantidade primeiro
    const orderedByQtyDesc = [...product.EMBALAGEM].sort((a, b) => {
      const aQty = (a.QTD_MULTIPLO_VENDA || 1) * (a.QTD_EMBALAGEM || 1);
      const bQty = (b.QTD_MULTIPLO_VENDA || 1) * (b.QTD_EMBALAGEM || 1);
      return bQty - aQty;
    });

    const withPriceAndStock = orderedByQtyDesc.find((emb) => {
      const qty = emb.QTD_MULTIPLO_VENDA > 1 ? emb.QTD_MULTIPLO_VENDA : emb.QTD_EMBALAGEM;
      const rec = findPriceRecordForEmb(emb);
      const hasAnyPrice = Boolean(rec?.VLR_FINAL || rec?.VLR_PRECO);
      return hasAnyPrice && remainingUnits >= (qty || 0);
    });

    // Se o pack atual é válido mas existe um com maior quantidade (e com preço + estoque), prioriza o maior
    if (canKeepCurrent && withPriceAndStock) {
      const bestQty =
        withPriceAndStock.QTD_MULTIPLO_VENDA > 1 ? withPriceAndStock.QTD_MULTIPLO_VENDA : withPriceAndStock.QTD_EMBALAGEM;
      if ((bestQty || 0) > (currentQty || 0)) {
        setPackage(withPriceAndStock.SG_EMBALAGEM as PackagesTypesProps);
        setPackQuantity(bestQty || 0);
        isChangedPack.current = true;
      }
      return;
    }

    // Se encontrou uma embalagem disponível, seleciona; senão mantém a atual (evita selecionar indisponível)
    if (withPriceAndStock) {
      const qty =
        withPriceAndStock.QTD_MULTIPLO_VENDA > 1 ? withPriceAndStock.QTD_MULTIPLO_VENDA : withPriceAndStock.QTD_EMBALAGEM;
      setPackage(withPriceAndStock.SG_EMBALAGEM as PackagesTypesProps);
      setPackQuantity(qty || 0);
      isChangedPack.current = true;
    }
  }, [
    product,
    productPrices,
    modePayment,
    discountPrice,
    price?.VLR_FINAL,
    effectiveFinalPrice,
    hasStock,
    getLimit,
    totalQuantity,
    stockLeft,
    pack,
    packQuantity,
    packing?.QTD_EMBALAGEM,
    packing?.QTD_MULTIPLO_VENDA,
  ]);

  return (
    <div style={{ position: 'relative', height: '100%' }} id={`product-${product.COD_PRODUTO}`}>
      <S.ProductCard $showSelo={showSelo && hasStock}>
        <S.ProductContent direction={direction}>
          <S.Container>
            <S.ContentWrapper>
              <S.InfoRow>
                <S.EanWrapper $isList={direction === 'list'}>
                  <S.ProductCode
                    onClick={() => {
                      if (sku) {
                        copyToClipboard({
                          value: sku.toString(),
                          title: `Cód. <b>${sku}</b> copiado com sucesso`,
                        });
                      }
                    }}
                    $showSelo={showSelo && hasStock}
                  >
                    {sku}
                  </S.ProductCode>
                  <S.ProductCode
                    $showSelo={showSelo && hasStock}
                    onClick={() => {
                      if (ean) {
                        copyToClipboard({
                          value: ean,
                          title: `EAN <b>${ean}</b> copiado com sucesso`,
                        });
                      }
                    }}
                  >
                    {ean}
                  </S.ProductCode>
                </S.EanWrapper>
                <S.CommissionFlag $showSelo={showSelo && hasStock}>{commissionFlag}</S.CommissionFlag>
              </S.InfoRow>

              <S.ContentRow $isList={direction === 'list'}>
                <S.PaddingImage direction={direction}>
                  <S.ContentImage to={`/${product.COD_PRODUTO}`} onClick={onProductNavigate}>
                    <S.ProductImage
                      alt={product.DESC_PRODUTO}
                      id={`product-image-${product.COD_PRODUTO}`}
                      src={buildImageSrc(image)}
                      onError={buildImageSrcError}
                      loading="lazy"
                      decoding="async"
                    />
                  </S.ContentImage>
                </S.PaddingImage>

                <S.Div>
                  <ProductInfo
                    product={product}
                    ean={ean}
                    commissionFlag={commissionFlag}
                    cardDescription={cardDescription}
                    onProductNavigate={onProductNavigate}
                  />

                  {showInfos && (
                    <S.AddList
                      data-test={`open-modal-custom-attributes-${product.COD_PRODUTO}`}
                      onClick={handleOpenAttributesModal}
                    >
                      <S.BtnAddList $grey={1} $hasExpiring={!!hasExpiring} $expiringStatus={expiringSeverity}>
                        <RiInformationLine
                          size={20}
                          color={
                            hasExpiring && expiringSeverity === 'warning'
                              ? colors.alert.orange300
                              : hasExpiring && expiringSeverity === 'danger'
                                ? colors.alert.red200
                                : colors.brand.grafite
                          }
                        />
                      </S.BtnAddList>
                      {hasExpiring && (
                        <S.ExpiringText $severity={expiringSeverity}>
                          <RiCalendarTodoLine size={16} />
                          {getStockMsg(product?.COD_PRODUTO)}
                        </S.ExpiringText>
                      )}
                    </S.AddList>
                  )}
                </S.Div>
              </S.ContentRow>

              <S.CustomDiv>
                {/* AQUI: fullBorder também quando NÃO tiver estoque */}
                <Packaging
                  showQtd
                  pack={pack}
                  packQty={packQuantity}
                  product={product}
                  canDisablePack={false}
                  setPackage={setPackageIntercept}
                  setPackQuantity={setPackQuantityIntercept}
                  fullBorder={!effectiveFinalPrice || !hasStock}
                />

                {effectiveFinalPrice > 0 && hasStock && (
                  <PriceContent
                    discountPrice={discountPrice || 0}
                    selectedClient={selectedClient}
                    uid={uid}
                    discounts={discounts}
                    showFlex={showFlex}
                    showInfosFlex={showInfosFlex}
                    handleOpenFlexModal={handleOpenFlexModal}
                    product={product}
                    showOldPrice={showOldPrice}
                    hasStock={hasStock}
                    price={price}
                    unityPrice={unityPrice}
                    effectiveFinalPrice={effectiveFinalPrice}
                    packQty={packRequiredQty || 0}
                    effectiveRecord={effectiveRecord}
                    rawQuantity={rawQuantity}
                    packQuantity={packQuantity}
                    direction={direction}
                    flexState={flexState}
                    flexPercentual={flexPercentual}
                  />
                )}

                <LastPurchase lastPurchase={lastPurchase} direction={direction} label="itens" />

                <AddToCart
                  pack={pack}
                  label="Adicionar"
                  product={product}
                  background="light"
                  packQty={packQuantity}
                  id={`${product.COD_PRODUTO}`}
                  unavailabel={!(effectiveFinalPrice > 0) || !hasStock}
                />
              </S.CustomDiv>
            </S.ContentWrapper>
          </S.Container>
        </S.ProductContent>
      </S.ProductCard>
    </div>
  );
};

export default memo(RenderProducts);
